import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface BankStatement {
  id: number;
  bank_account: string;
  statement_date: string;
  ending_balance: number;
  book_balance: number;
  difference: number;
  status: 'pending' | 'in_progress' | 'completed';
}

// Placeholder data
const statements: BankStatement[] = [
  { id: 1, bank_account: 'Operating Bank Account', statement_date: '2026-03-31', ending_balance: 15420.50, book_balance: 15420.50, difference: 0, status: 'completed' },
  { id: 2, bank_account: 'Operating Bank Account', statement_date: '2026-02-28', ending_balance: 12350.75, book_balance: 12350.75, difference: 0, status: 'completed' },
  { id: 3, bank_account: 'Savings Account', statement_date: '2026-03-31', ending_balance: 50000.00, book_balance: 49980.00, difference: 20.00, status: 'in_progress' },
];

interface UnreconciledItem {
  id: number;
  date: string;
  description: string;
  amount: number;
  type: 'deposit' | 'withdrawal';
}

const unreconciledItems: UnreconciledItem[] = [
  { id: 1, date: '2026-04-01', description: 'Deposit - Client ABC', amount: 2500.00, type: 'deposit' },
  { id: 2, date: '2026-04-02', description: 'EFT Payment - Supplier XYZ', amount: -890.50, type: 'withdrawal' },
  { id: 3, date: '2026-04-02', description: 'Bank Fee', amount: -15.00, type: 'withdrawal' },
];

export function Reconciliation() {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  const getStatusVariant = (status: BankStatement['status']): 'secondary' | 'outline' | 'default' => {
    switch (status) {
      case 'completed': return 'secondary';
      case 'in_progress': return 'outline';
      default: return 'default';
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bank Reconciliation</h1>
          <p className="text-sm text-muted-foreground">Match bank statements to book records</p>
        </div>
        <Button variant="default" size="sm">
          <i className="bi bi-plus"></i> New Reconciliation
        </Button>
      </div>

      {/* Unreconciled Items Alert */}
      {unreconciledItems.length > 0 && (
        <Card className="border-[var(--accent)] bg-[var(--accent-bg)]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <i className="bi bi-exclamation-triangle text-[var(--accent)] text-xl mt-0.5"></i>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{unreconciledItems.length} unreconciled transactions</p>
                <p className="text-sm text-muted-foreground">These transactions need to be matched to your bank statement.</p>
              </div>
              <Button variant="outline" size="sm" className="border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-bg)]">
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Reconciliation */}
      <Card>
        <CardHeader>
          <CardTitle>Active Reconciliation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Bank Statement Balance</span>
              <span className="text-xl font-mono font-semibold">$15,420.50</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Book Balance</span>
              <span className="text-xl font-mono font-semibold">$15,400.50</span>
            </div>
            <div className="flex flex-col gap-1 p-3 bg-[var(--accent-bg)] rounded-lg border border-[var(--accent)]">
              <span className="text-sm text-[var(--accent)]">Difference</span>
              <span className="text-xl font-mono font-semibold text-foreground">$20.00</span>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold mb-3">Deposits in Transit</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                      <th className="py-2 px-3 text-center font-medium text-muted-foreground">
                        <input type="checkbox" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unreconciledItems.filter(i => i.type === 'deposit').map((item) => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3 font-mono">{formatDate(item.date)}</td>
                        <td className="py-2 px-3">{item.description}</td>
                        <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(item.amount)}</td>
                        <td className="py-2 px-3 text-center"><input type="checkbox" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-3">Outstanding Checks</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
                      <th className="py-2 px-3 text-center font-medium text-muted-foreground">
                        <input type="checkbox" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unreconciledItems.filter(i => i.type === 'withdrawal').map((item) => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3 font-mono">{formatDate(item.date)}</td>
                        <td className="py-2 px-3">{item.description}</td>
                        <td className="py-2 px-3 text-right font-mono text-red-600">-{formatCurrency(item.amount)}</td>
                        <td className="py-2 px-3 text-center"><input type="checkbox" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="outline">Save Progress</Button>
            <Button variant="default">Complete Reconciliation</Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Reconciliations */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Reconciliations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Bank Account</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Statement Date</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Ending Balance</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Book Balance</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Difference</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((stmt) => (
                  <tr key={stmt.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">{stmt.bank_account}</td>
                    <td className="py-3 px-4 font-mono">{formatDate(stmt.statement_date)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(stmt.ending_balance)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(stmt.book_balance)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${stmt.difference === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stmt.difference === 0 ? '—' : formatCurrency(stmt.difference)}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(stmt.status)}>
                        {stmt.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm">
                        <i className="bi bi-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}